'use client'

import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useMemo, useSyncExternalStore } from 'react'
import { Link, usePathname } from '@/i18n/navigation'

const SCROLL_Y = 24

function subscribeScroll(cb: () => void) {
    window.addEventListener('scroll', cb, { passive: true })
    window.addEventListener('resize', cb, { passive: true })
    return () => {
        window.removeEventListener('scroll', cb)
        window.removeEventListener('resize', cb)
    }
}

function getScrollSnapshot() {
    return window.scrollY || 0
}

function getServerSnapshot() {
    return 0
}

export default function Header() {
    const t = useTranslations('header')
    const pathname = usePathname()
    const isHome = pathname === '/'

    const scrollY = useSyncExternalStore(subscribeScroll, getScrollSnapshot, getServerSnapshot)
    const scrolled = !isHome ? true : scrollY > SCROLL_Y

    const chrome = useMemo(() => {
        if (isHome && !scrolled) {
            return {
                headerClass: 'bg-transparent shadow-none',
                innerClass: 'py-8 sm:py-7',
                logoClass: 'h-14 sm:h-[72px]'
            } as const
        }

        return {
            headerClass: '',
            innerClass: 'py-4',
            logoClass: 'h-12 sm:h-14'
        } as const
    }, [isHome, scrolled])

    // ✅ tune these yourself
    const HUD_BRAND = '#144738'
    const HUD_BLACK_ALPHA = 0.35 // try 0.25 (lighter) .. 0.50 (darker)

    return (
        <header
            aria-label={t('ariaLabel')}
            className={[
                'fixed inset-x-0 top-0 z-50',
                'pt-[env(safe-area-inset-top)]',
                'transition-[background,box-shadow,backdrop-filter] duration-300',
                chrome.headerClass
            ].join(' ')}
            style={
                scrolled
                    ? ({
                          // brand base + black overlay (easy to tune)
                          background: `linear-gradient(rgba(0,0,0,${HUD_BLACK_ALPHA}), rgba(0,0,0,${HUD_BLACK_ALPHA})), ${HUD_BRAND}`,
                          boxShadow: 'var(--shadow-md)',
                          backdropFilter: 'blur(14px)',
                          WebkitBackdropFilter: 'blur(14px)'
                      } as React.CSSProperties)
                    : undefined
            }>
            <div
                className={[
                    'container-wide flex items-center justify-between gap-4',
                    'transition-[padding] duration-300',
                    chrome.innerClass
                ].join(' ')}>
                <Link href='/' className='inline-flex items-center' aria-label={t('home')}>
                    <Image
                        src='/logo/ai_isudo_Logo_RGB_Word_White.svg'
                        alt='ISUDO GmbH'
                        width={520}
                        height={104}
                        priority
                        className={[
                            'w-auto',
                            'transition-[height] duration-300',
                            'drop-shadow-[0_14px_28px_rgba(0,0,0,0.34)]',
                            chrome.logoClass
                        ].join(' ')}
                    />
                </Link>

                <Link
                    href='/#kontakt'
                    className='inline-flex h-11 items-center justify-center rounded-2xl bg-white/12 px-5 text-sm font-semibold tracking-tight text-white shadow-(--shadow-sm) backdrop-blur-sm transition-colors hover:bg-white/20 focus-visible:shadow-(--focus) focus-visible:outline-none'>
                    {t('contact')}
                </Link>
            </div>
        </header>
    )
}
